import {
  GetPresignedUrlForUploadingType,
  UploadLicenseDocumentType,
  getCandidateDocumentQuery,
  saveCandidateDocumentDotMedicalMutation,
  saveCandidateDocumentDriverLicenseMutation,
  CheckPreProcessingStatusQuery,
  CreateDocumentRequestQuery,
  saveDocumentPdfToServerMutation,
  AddDocumentToRequestMutation,
  getCandidateDocumentByIdQuery,
  createBasicUploadRequestMutation,
  resetDocumentFilesMutation,
} from './interfaces';
import { apiService, driverDocumentsTag } from '../api';
import axios from 'axios';
import { getAccessToken } from 'functions/localStorage';
import config from 'config/environment';

function prepareFileTypesQueryParams(params: { fileTypes?: string[] }) {
  if (!params.fileTypes || !params.fileTypes.length) {
    return '';
  }
  const res = [];
  for (const fileType of params.fileTypes) {
    if (fileType) {
      res.push(`fileTypes[]=${fileType}`);
    }
  }
  return res.length ? `?${res.join('&')}` : '';
}

const documentRequestCandidateService = apiService.injectEndpoints({
  endpoints: (builder) => ({
    getCandidateDocumentList: builder.query<
      getCandidateDocumentQuery['response'],
      getCandidateDocumentQuery['request']
    >({
      query: (query) => ({
        url: `/document-request/candidate${query ? query : ''}`,
        method: 'GET',
      }),
      providesTags: [driverDocumentsTag],
      keepUnusedDataFor: 10,
    }),
    getCandidateDocumentById: builder.query<
      getCandidateDocumentByIdQuery['response'],
      getCandidateDocumentByIdQuery['request']
    >({
      query: ({ documentId }) => ({
        url: `/document/candidate/get-by-id/${documentId}`,
        method: 'GET',
      }),
      providesTags: [driverDocumentsTag],
      keepUnusedDataFor: 10,
    }),
    uploadCandidateDriverLicense: builder.mutation<
      saveCandidateDocumentDriverLicenseMutation['response'],
      saveCandidateDocumentDriverLicenseMutation['request']
    >({
      async queryFn(
        { data, documentRequestId },
        _api,
        _extraOptions,
        baseQuery
      ) {
        const result = await baseQuery({
          url: `/document-request/candidate/upload-driver-license/${documentRequestId}`,
          method: 'POST',
          body: data,
        });

        if (result.error) {
          return { error: result.error };
        }

        return { data: result.data as any };
      },
      invalidatesTags: [driverDocumentsTag],
    }),
    uploadCandidateDotMedical: builder.mutation<
      saveCandidateDocumentDotMedicalMutation['response'],
      saveCandidateDocumentDotMedicalMutation['request']
    >({
      async queryFn(
        { file, documentRequestId },
        _api,
        _extraOptions,
        baseQuery
      ) {
        const body = new FormData();
        body.append('file', file);
        const result = await baseQuery({
          url: `/document-request/candidate/upload-dot-medical/${documentRequestId}`,
          method: 'POST',
          body: body,
        });

        if (result.error) {
          return { error: result.error };
        }

        return { data: result.data as any };
      },
      invalidatesTags: [driverDocumentsTag],
    }),

    getPresignedUrlForUploading: builder.query<
      GetPresignedUrlForUploadingType['response'],
      GetPresignedUrlForUploadingType['request']
    >({
      query: (params) => ({
        url: `/document-request/candidate/upload-url/${
          params.documentId
        }${prepareFileTypesQueryParams(params)}`,
        method: 'GET',
      }),
      providesTags: [driverDocumentsTag],
    }),

    resetDocumentFiles: builder.mutation<
      resetDocumentFilesMutation['response'],
      resetDocumentFilesMutation['request']
    >({
      query: (params) => ({
        url: `/document-request/candidate/reset-document-files/${params.documentId}`,
        method: 'POST',
      }),
      invalidatesTags: [driverDocumentsTag],
    }),

    checkPreProcessingStatus: builder.query<
      CheckPreProcessingStatusQuery['response'],
      CheckPreProcessingStatusQuery['request']
    >({
      query: (params) => ({
        url: `/document-request/candidate/check-pre-processing-status/${params.documentId}`,
        method: 'GET',
      }),
    }),

    createBasicUploadRequest: builder.mutation<
      createBasicUploadRequestMutation['response'],
      createBasicUploadRequestMutation['request']
    >({
      query: (body) => ({
        url: '/document-request/candidate/create-basic-upload-request',
        method: 'POST',
        body,
      }),
    }),

    uploadLicenseDocument: builder.mutation<
      UploadLicenseDocumentType['response'],
      UploadLicenseDocumentType['request']
    >({
      async queryFn({ file, url, onProgress }) {
        if (!url) {
          console.error('URL to upload the document is empty!');
        }

        const headers: any = {
          'Content-Type': file.type,
        };

        if (url.startsWith(config.API_URL)) {
          headers['Authorization'] = `Bearer ${getAccessToken()}`;
        }

        const result = await axios.put(url, file, {
          headers,
          onUploadProgress: (upload) => {
            const uploadloadProgress = upload.total
              ? Math.round((100 * upload.loaded) / upload.total)
              : 0;
            onProgress?.(uploadloadProgress);
          },
        });

        if (result.data.error) {
          return { error: result.data.error };
        }

        return { data: result.data as any };
      },
    }),

    createDocumentRequest: builder.mutation<
      CreateDocumentRequestQuery['response'],
      CreateDocumentRequestQuery['request']
    >({
      query: (params) => ({
        url: `/document-request/candidate/document/${params.documentType}`,
        method: 'POST',
      }),
      invalidatesTags: [driverDocumentsTag],
    }),

    addDocumentToRequest: builder.mutation<
      AddDocumentToRequestMutation['response'],
      AddDocumentToRequestMutation['request']
    >({
      query: (body) => ({
        url: '/document-request/candidate/add-to-request',
        method: 'POST',
        body,
      }),
      invalidatesTags: [driverDocumentsTag],
    }),

    saveDocumentPdfToServer: builder.mutation<
      saveDocumentPdfToServerMutation['response'],
      saveDocumentPdfToServerMutation['request']
    >({
      query: ({ candidateDocumentId, filename }) => ({
        url: `/document/manager/sync-to-open-api-subscription/${candidateDocumentId}`,
        method: 'POST',
        body: {
          filename,
        },
      }),
    }),
  }),
});

export default documentRequestCandidateService;
